import axios from 'axios'
import { createApiUrl } from '../config/api'

const baseUrl = createApiUrl('/auth/token/')
const revokeUrl = createApiUrl('/auth/revoke/')

var token = null
var refresh_token = null

const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

const setToken = new_token => {
    token = new_token
}

const setRefreshToken = new_refresh_token => {
    refresh_token = new_refresh_token
}

const getToken = () => (token !== null)? `Bearer ${token}` : null

const getAccessToken = () => token

const getRefreshToken = () => refresh_token

// Headers for endpoints that must NOT see an Authorization header. The OAuth
// endpoints (/auth/token/, /auth/convert-token/) authenticate with the body's
// credentials; a stale or expired bearer makes them reject the request with
// 403 "Invalid token header" before the credentials are ever looked at.
const getAnonHeader = () => ({
    headers: {
        'X-CSRFToken': getCookie('csrftoken')
    }
})

const getAuthHeader = () => {
    const auth = getAnonHeader()
    if(getToken() !== null) auth.headers.Authorization = getToken()

    return auth
}

const refresh = async () => {
    const data = {
        grant_type: 'refresh_token',
        client_id: process.env.REACT_APP_REGULAR_LOGIN_CLIENT_ID,
        client_secret: process.env.REACT_APP_REGULAR_LOGIN_CLIENT_SECRET,
        refresh_token: refresh_token
    }

    const res = await axios.post(baseUrl, data, getAnonHeader())
    return res.data
}

const refreshGoogle = async () => {
    const data = {
        grant_type: 'refresh_token',
        client_id: process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID,
        client_secret: process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_SECRET,
        refresh_token: refresh_token
    }

    const res = await axios.post(baseUrl, data, getAnonHeader())
    return res.data
}

// Logout goes to /auth/revoke/ — django-oauth-toolkit's RFC 7009 endpoint.
//
// It authenticates the CLIENT (client_id + client_secret in the form body), not
// the user, which is what makes logout reliable:
//   * an access token that has already lapsed can still be revoked — the old
//     /auth/invalidate-sessions/ needed a currently-valid bearer, so the one
//     case where you most want to end a session was the one it refused;
//   * revoking the refresh token cascades to the access token it issued, so the
//     session is actually over. invalidate-sessions deleted access tokens only
//     and left the refresh token minting replacements for another 48 hours;
//   * per RFC 7009 an unknown or already-revoked token is still a 200, so
//     logging out of a session that is already gone is not an error.
//
// The endpoint reads a form-encoded body (it is a plain Django view, not DRF),
// hence URLSearchParams rather than a JSON object.
const revokeOne = async (clientId, clientSecret, tokenValue, hint) => {
    const body = new URLSearchParams({
        client_id: clientId,
        client_secret: clientSecret,
        token: tokenValue,
        token_type_hint: hint,
    })

    await axios.post(revokeUrl, body, getAnonHeader())
}

// Both tokens are passed in explicitly: logout clears the module-level copies
// synchronously (so nothing can reuse a half-revoked session) and hands the
// captured values here.
const revokeSession = async (clientId, clientSecret, accessToken, refreshToken, label) => {
    try {
        // The refresh token is the long-lived credential and cascades to its
        // access token; the access token is then revoked directly as well, to
        // cover a rotated pair whose link the server has already broken.
        if(refreshToken) await revokeOne(clientId, clientSecret, refreshToken, 'refresh_token')
        if(accessToken) await revokeOne(clientId, clientSecret, accessToken, 'access_token')

        return true
    } catch (err) {
        // Developer diagnostic only. The user is signed out on this device
        // either way, so this must never become a user-facing message.
        console.warn(`Logout: the server did not confirm ${label} token revocation`, err)
        return false
    }
}

const revokeTokens = (accessToken = token, refreshToken = refresh_token) =>
    revokeSession(
        process.env.REACT_APP_REGULAR_LOGIN_CLIENT_ID,
        process.env.REACT_APP_REGULAR_LOGIN_CLIENT_SECRET,
        accessToken, refreshToken, 'session',
    )

const revokeGoogleTokens = (accessToken = token, refreshToken = refresh_token) =>
    revokeSession(
        process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_ID,
        process.env.REACT_APP_GOOGLE_LOGIN_CLIENT_SECRET,
        accessToken, refreshToken, 'Google session',
    )

const tokenService = { setToken, setRefreshToken, getToken, getAccessToken, getRefreshToken, getAuthHeader, getAnonHeader, refresh, revokeTokens, revokeGoogleTokens, refreshGoogle }

export default tokenService